import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import {
  getSelfDataApi,
  getAllDataApi,
  getSystemStatusApi,
  getTodayTimeRange,
  type DataQueryParams,
} from '@/api/dashboard'
import { getApiKeysApi } from '@/api/apiKeys'
import { useAuthStore } from '@/stores/authStore'
import {
  getDefaultFilterRange,
  findMatchingQuickPreset,
  getQuickTimeRange,
  type DashboardQuickPreset,
  type DashboardFilterState,
} from '@/utils/timeRange'

export type DashboardChartTab =
  | 'quota-dist'
  | 'calls-trend'
  | 'calls-dist'
  | 'model-quota-rank'
  | 'model-calls-rank'
  | 'user-quota-rank'
  | 'user-quota-trend'

function buildDataQueryParams(
  start: number,
  end: number,
  tokenId: number | null
): DataQueryParams {
  const params: DataQueryParams = {
    start_timestamp: start,
    end_timestamp: end,
  }
  if (tokenId != null && tokenId > 0) {
    params.token_id = tokenId
  }
  return params
}

/**
 * 获取用量趋势数据（按日期统计）
 */
export function useQuotaData(params?: DataQueryParams) {
  const isAdmin = useAuthStore((state) => state.user?.role ?? 0) >= 10

  return useQuery({
    queryKey: ['quotaData', params, isAdmin],
    queryFn: () => {
      if (isAdmin) {
        return getAllDataApi(params)
      }
      return getSelfDataApi(params)
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  })
}

/**
 * Dashboard 筛选条用：拉取当前用户 API Key 列表
 */
export function useDashboardTokens() {
  return useQuery({
    queryKey: ['dashboardTokens'],
    queryFn: async () => {
      const res = await getApiKeysApi({ page: 1, page_size: 1000 })
      return res.items || []
    },
    staleTime: 300_000,
  })
}

/**
 * 获取系统状态
 */
export function useSystemStatus() {
  return useQuery({
    queryKey: ['systemStatus'],
    queryFn: () => getSystemStatusApi(),
    staleTime: 300000, // 5 分钟
  })
}

/**
 * Dashboard 数据管理 Hook
 * 整合所有 Dashboard 数据获取逻辑
 */
export function useDashboard() {
  const [filter, setFilter] = useState<DashboardFilterState>(getDefaultFilterRange)
  const [activeChartTab, setActiveChartTab] = useState<DashboardChartTab>('quota-dist')

  const timeParams: DataQueryParams = useMemo(
    () => buildDataQueryParams(filter.start, filter.end, filter.tokenId),
    [filter.start, filter.end, filter.tokenId]
  )

  const setQuickPreset = useCallback((p: DashboardQuickPreset) => {
    if (p === 'custom') {
      setFilter((prev) => ({ ...prev, activePreset: 'custom' }))
      return
    }
    const r = getQuickTimeRange(p)
    setFilter((prev) => ({ ...prev, start: r.start, end: r.end, activePreset: p }))
  }, [])

  const setFilterRange = useCallback((start: number, end: number) => {
    const m = findMatchingQuickPreset(start, end)
    setFilter((prev) => ({ ...prev, start, end, activePreset: m ?? 'custom' }))
  }, [])

  const setTokenId = useCallback((tokenId: number | null) => {
    setFilter((prev) => ({ ...prev, tokenId }))
  }, [])

  // 顶栏「今日*」：当日 0 点 ～ 当前（与 API Key 筛选联动）
  const todayRange = getTodayTimeRange()
  const todayParams = useMemo(
    () => buildDataQueryParams(todayRange.start, todayRange.end, filter.tokenId),
    [todayRange.start, todayRange.end, filter.tokenId]
  )

  const { data: quotaData, isLoading: quotaLoading } = useQuotaData(todayParams)

  // 与筛选区联动的趋势数据
  const { data: trendData, isLoading: trendLoading } = useQuotaData(timeParams)

  const { isLoading: statusLoading } = useSystemStatus()

  const stats = useMemo(() => {
    if (!quotaData) return null

    const totalQuota = quotaData.reduce((sum, item) => sum + (item.quota || 0), 0)
    const totalTokens = quotaData.reduce((sum, item) => sum + (item.token_count || 0), 0)
    const totalRequests = quotaData.reduce((sum, item) => sum + (item.request_count || 0), 0)

    return {
      todayCalls: totalRequests,
      todayTokens: totalTokens,
      todayQuota: totalQuota,
    }
  }, [quotaData])

  return {
    stats,
    quotaData,
    trendData,

    isLoading: quotaLoading || trendLoading || statusLoading,
    quotaLoading,
    trendLoading,

    filter,
    setQuickPreset,
    setFilterRange,
    setTokenId,
    timeParams,
    todayParams,

    activeChartTab,
    setActiveChartTab,
  }
}
